import { Directive, Input, OnInit, OnDestroy, AfterViewInit, ContentChildren, QueryList } from '@angular/core';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TabUrlKeyDirective } from './tab-url-key.directive';

/**
 * Directive that synchronizes mat-tab-group selection with URL query parameters.
 *
 * Usage with urlKey directive (recommended):
 * <mat-tab-group syncTabsWithUrl>
 *   <mat-tab label="Planning" urlKey="planning">...</mat-tab>
 *   <mat-tab label="Driver View" urlKey="driver">...</mat-tab>
 *   <mat-tab label="Flights" urlKey="flights">...</mat-tab>
 * </mat-tab-group>
 *
 * This will create URLs like: ?tab=planning, ?tab=driver, ?tab=flights
 *
 * If no urlKey is provided, auto-generates from label (kebab-case)
 */
@Directive({
  selector: '[syncTabsWithUrl]',
  standalone: false
})
export class TabQuerySyncDirective implements AfterViewInit, OnDestroy {
  /** The URL query parameter name (e.g., 'view', 'tab', 'section') */
  @Input() urlParam: string = 'tab';

  /** The default tab to select if no valid query param is found */
  @Input() defaultTab: number = 0;

  @ContentChildren(MatTab) tabs!: QueryList<MatTab>;
  @ContentChildren(TabUrlKeyDirective) urlKeyDirectives!: QueryList<TabUrlKeyDirective>;

  private destroy$ = new Subject<void>();
  private queryValuesByIndex: { [tabIndex: number]: string } = {};
  private tabNamesByQuery: { [queryValue: string]: number } = {};
  private isInitializing = true;

  constructor(
    private tabGroup: MatTabGroup,
    private route: ActivatedRoute,
    private router: Router
  ) {}


  ngAfterViewInit(): void {
    setTimeout(() => {
      this.buildTabMapping();
      this.startSyncing();
    }, 0);
  }

  private buildTabMapping(): void {
    const urlKeyArray = this.urlKeyDirectives.toArray();

    this.tabs.forEach((tab, index) => {
      let queryValue: string;

      const urlKeyDirective = urlKeyArray[index];

      if (urlKeyDirective && urlKeyDirective.urlKey) {
        queryValue = urlKeyDirective.urlKey;
      } else if (tab.textLabel) {
        queryValue = this.toKebabCase(tab.textLabel);
      } else {
        queryValue = `tab-${index}`;
      }

      this.queryValuesByIndex[index] = queryValue;
      this.tabNamesByQuery[queryValue] = index;
    });
  }


  private toKebabCase(str: string): string {
    return str
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Sets up bidirectional sync between URL query params and tab selection
   */
  private startSyncing(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const queryValue = params[this.urlParam];
        const matchedTabIndex = queryValue ? this.findTabIndexByQueryValue(queryValue) : null;

        if (matchedTabIndex !== null) {
          this.tabGroup.selectedIndex = matchedTabIndex;
        } else {
          this.tabGroup.selectedIndex = this.getValidDefaultTab();
        }

        setTimeout(() => {
          this.isInitializing = false;
        }, 0);
      });

    this.tabGroup.selectedIndexChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(tabIndex => {
        if (!this.isInitializing) {
          this.syncUrlWithTab(tabIndex);
        }
      });
  }

  private findTabIndexByQueryValue(queryValue: string): number | null {
    const tabIndex = this.tabNamesByQuery[queryValue];

    if (tabIndex !== undefined && tabIndex < this.tabGroup._tabs.length) {
      return tabIndex;
    }

    return null;
  }

  private getValidDefaultTab(): number {
    const totalTabs = this.tabGroup._tabs.length;
    return Math.min(this.defaultTab, Math.max(0, totalTabs - 1));
  }

  private syncUrlWithTab(tabIndex: number): void {
    const queryValue = this.queryValuesByIndex[tabIndex];

    if (queryValue) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { [this.urlParam]: queryValue },
        queryParamsHandling: 'merge'
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
